import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import type { CellHighlightTarget } from '@/components/data-table';
import type {
  DirtyFieldsMap,
  EquipmentBulkEditFormValues,
} from '@/features/equipment-browser/equipment-bulk-edit/equipment-bulk-edit.types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const parseDirtyFieldsValues = (dirtyValues: unknown): DirtyFieldsMap => {
  if (!isRecord(dirtyValues)) return undefined;

  const normalized: Record<string, Record<string, boolean | undefined>> = {};

  for (const [rowId, rowValue] of Object.entries(dirtyValues)) {
    if (!isRecord(rowValue)) continue;

    const rowDirty: Record<string, boolean | undefined> = {};
    for (const [columnId, columnValue] of Object.entries(rowValue)) {
      rowDirty[columnId] = typeof columnValue === 'boolean' ? columnValue : undefined;
    }

    normalized[rowId] = rowDirty;
  }

  return normalized;
};

export const buildDirtyCellTargets = (dirtyFields: DirtyFieldsMap): CellHighlightTarget[] => {
  if (!dirtyFields) return [];

  const targets: CellHighlightTarget[] = [];

  for (const rowId of Object.keys(dirtyFields)) {
    const rowDirty = dirtyFields[rowId];
    if (!rowDirty) continue;

    for (const columnId of Object.keys(rowDirty)) {
      if (!rowDirty[columnId]) continue;
      targets.push({ rowId, columnId });
    }
  }

  return targets;
};

export const useDirtyCellTargets = (): CellHighlightTarget[] => {
  const {
    formState: { dirtyFields },
  } = useFormContext<{ values: EquipmentBulkEditFormValues }>();

  const parsedDirtyFields = parseDirtyFieldsValues(dirtyFields?.values);
  const dirtySignature = JSON.stringify(parsedDirtyFields);

  return useMemo(() => buildDirtyCellTargets(parsedDirtyFields), [dirtySignature]);
};
